import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import type { IssueTypeHierarchyLevelType } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import type { WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS } from '../view/whiteboards-entrypoint-experiment/constants.tsx';
import type {
	REFRESH_SOURCE_ACTIVE_TAB,
	BACKLOG,
	BOARD,
	ACTIVE_SPRINT,
	INACTIVE_SPRINT,
	SPRINT,
	SPRINT_STATE_FUTURE,
	SPRINT_STATE_ACTIVE,
	REFRESH_SOURCE_FAILURE,
	REFRESH_SOURCE_REALTIME,
	REFRESH_SOURCE_GLOBAL_ISSUE_CREATE,
	REFRESH_SOURCE_SPRINT_COMPLETE,
	EDIT_BOARD_CONFIG,
	DELETE_ISSUE,
	CREATE_ISSUE,
	MANAGE_SPRINT,
	SPRINT_MOVE_TO_TOP,
	SPRINT_MOVE_DOWN,
	SPRINT_MOVE_UP,
	SPRINT_MOVE_TO_BOTTOM,
	EDIT_ISSUE,
	ASSIGN_ISSUE,
	BULK_CHANGE,
	TRANSITION_ISSUE,
	ADMINISTER_BOARD,
	VIEW_BOARD,
	ISSUE_COUNT,
	ORIGINAL_ESTIMATE,
	STORY_POINTS,
	REFRESH_SOURCE_STATUS_REMAPPED,
	REFRESH_SOURCE_ON_MOUNT,
	NONE_BACKLOG_STRATEGY,
	SPRINT_BACKLOG_STRATEGY,
	ISSUE_LIST_BACKLOG_STRATEGY,
	KANBAN_BACKLOG_STRATEGY,
	REFRESH_SOURCE_POLLING,
	CARD_TYPE,
	GROUP_TYPE,
	REFRESH_SOURCE_KANBAN_CARD_MOVE_SUCCESS,
	REFRESH_SOURCE_ADD_ISSUE_TYPE_MODAL,
	ARCHIVE_ISSUE,
} from './constants.tsx';
import type { ExtraField } from './extra-fields/types.tsx';

export type CardId = number;
export type CardIdAsObjectKey = string;
export type CardKey = string;
export type PersonId = string;
export type CardTypeId = number;
export type SprintId = number;
export type CardListId = string;
export type Person = {
	id: PersonId;
	displayName: string;
	avatarUrl: string | undefined;
};

export type SprintState = typeof SPRINT_STATE_FUTURE | typeof SPRINT_STATE_ACTIVE | string;

export type Sprint = {
	readonly goal: string;
	readonly startDate: string | null;
	readonly endDate: string | null;
	readonly daysRemaining: number | null;
	readonly state: SprintState;
	readonly canUpdateSprint: boolean;
};

// This is similar to CardListType but it is only used for analytics purposes since we need more granular information
export type CardListAnalyticsType =
	| typeof BACKLOG
	| typeof BOARD
	| typeof ACTIVE_SPRINT
	| typeof INACTIVE_SPRINT;

export type CardListType = typeof BACKLOG | typeof BOARD | typeof SPRINT;

export type CardList = {
	readonly id: string;
	readonly name: string;
	readonly type: CardListType;
	readonly issueListKey: string | null;
	readonly sprintData?: Sprint;
	readonly isOptimistic?: boolean;
	readonly minColumnLimit?: number;
	readonly maxColumnLimit?: number;
};

export type PartialCard = {
	readonly id: number;
	readonly key: string;
	readonly parentId: number | null;
	readonly projectId: number;
};

export type PartialCardForVersionsPanel = PartialCard & {
	readonly fixVersions?: string[];
};

export type CardPriority = {
	readonly name: string;
	readonly iconUrl: string;
};

export type CardEstimate = {
	readonly storyPoints: number | null;
	readonly originalEstimate: {
		readonly value: number;
		readonly valueAsText: string;
	} | null;
	readonly timeRemainingEstimate: {
		readonly value: number;
		readonly valueAsText: string;
	} | null;
};

export type Card = {
	readonly summary: string;
	readonly id: CardId;
	readonly key: string;
	readonly labels: string[];
	readonly assignee: Person | null;
	readonly icon: string;
	readonly isDone: boolean;
	readonly isFlagged: boolean;
	readonly typeId: number;
	readonly issueUrl: string;
	readonly statusId: string;
	readonly parentId: number | null;
	readonly parentKey?: string | null;
	readonly priority: CardPriority | null;
	readonly dueDate: string | null;
	readonly estimate: CardEstimate | null;
	readonly numCompleteChildren: number;
	readonly numTotalChildren: number;
	readonly fixVersions?: string[];
	readonly projectId: number;
	readonly color?: string;
	readonly epicColor?: string;
	readonly extraFields?: ExtraField[];
	readonly typeHierarchyLevel?: number;
	readonly hidden?: boolean;
	readonly mentions?: Mentions | null;
};

// If type Mention changes, also update  @atlassian/jira-platform-board-kit/src/common/types.tsx
type Mentions = {
	readonly latestUnreadCommentId: number | null;
	readonly hasUnreadIssueDescriptionMention: boolean;
};

export type CardIdentifiers = {
	id: CardId;
	key: string;
};

export type CardForFlagWithCommentModal = {
	readonly id: CardId;
	readonly key: string;
	readonly summary: string;
	readonly type: FilterCardType;
};

export type CardType = {
	readonly id: CardTypeId;
	readonly name: string;
	readonly iconUrl: string;
	readonly hasRequiredFields: boolean | null;
	readonly typeHierarchyLevel: IssueTypeHierarchyLevelType;
};

export type FilterCardType = {
	readonly id: string;
	readonly name: string;
	readonly iconUrl: string;
};

type CardEntryIssue = {
	type: typeof CARD_TYPE;
	cardId: number;
};

export type CardEntryGroup = {
	type: typeof GROUP_TYPE;
	parentId: number;
	cardIds: number[];
	isAttachedToParent: boolean;
	isCollapsed: boolean;
};

export type CardEntry = CardEntryIssue | CardEntryGroup;

export const isCardEntryGroup = (cardEntry: CardEntry): cardEntry is CardEntryGroup =>
	cardEntry?.type === 'group';

export const getCardEntryGroupKey = ({ parentId }: CardEntryGroup, cardListId: string) =>
	`cardGroup-${cardListId}-${parentId}`;

export type ConfigProps = {
	locale: Locale;
	baseUrl: string;
	rapidViewId: string;
	isCompanyManaged: boolean;
	xsrfToken: string;
	projectKey: string;
	projectId: number;
	projectName: string;
	cloudId: string;
	atlassianAccountId: string | null;
};

export type BacklogStrategy =
	| typeof NONE_BACKLOG_STRATEGY
	| typeof SPRINT_BACKLOG_STRATEGY
	| typeof ISSUE_LIST_BACKLOG_STRATEGY
	| typeof KANBAN_BACKLOG_STRATEGY;

export type Permission =
	| typeof VIEW_BOARD
	| typeof ADMINISTER_BOARD
	| typeof BULK_CHANGE
	| typeof EDIT_BOARD_CONFIG
	| typeof DELETE_ISSUE
	| typeof CREATE_ISSUE
	| typeof ASSIGN_ISSUE
	| typeof TRANSITION_ISSUE
	| typeof EDIT_ISSUE
	| typeof MANAGE_SPRINT
	| typeof ARCHIVE_ISSUE
	| string; // There are more permissions than declared here

export type ServicePermissions = Record<Permission, boolean>;

export type RefreshSource =
	| typeof REFRESH_SOURCE_FAILURE
	| typeof REFRESH_SOURCE_REALTIME
	| typeof REFRESH_SOURCE_GLOBAL_ISSUE_CREATE
	| typeof REFRESH_SOURCE_SPRINT_COMPLETE
	| typeof REFRESH_SOURCE_KANBAN_CARD_MOVE_SUCCESS
	| typeof REFRESH_SOURCE_STATUS_REMAPPED
	| typeof REFRESH_SOURCE_ON_MOUNT
	| typeof REFRESH_SOURCE_ACTIVE_TAB
	| typeof REFRESH_SOURCE_POLLING
	| typeof REFRESH_SOURCE_ADD_ISSUE_TYPE_MODAL;

export type SprintMoveDirection =
	| typeof SPRINT_MOVE_TO_TOP
	| typeof SPRINT_MOVE_UP
	| typeof SPRINT_MOVE_DOWN
	| typeof SPRINT_MOVE_TO_BOTTOM;

export type EstimationType = typeof ORIGINAL_ESTIMATE | typeof STORY_POINTS | typeof ISSUE_COUNT;

export type GlobalIssueCreateProps = {
	issueId: string;
	issueKey: string;
	issueTypeId: string;
	createdIssueDetails: {
		fields: {
			status: {
				id: string;
			};
			project: {
				id: string;
			};
			issuetype: {
				hierarchyLevel: number;
			};
		};
	};
};

export type Project = {
	readonly id: number;
	readonly key: string;
	readonly name: string;
	readonly isSimplified: boolean;
};

export type Operation = {
	readonly operation: string;
	readonly targetType: string;
};

export type Result = {
	readonly createdAt: string;
	readonly authorId: string;
	readonly homepageId: string;
	readonly icon: {
		path: string;
		apiDownloadLink: string;
	};
	readonly name: string;
	readonly key: string;
	readonly id: string;
	readonly type: 'global';
	readonly description: {
		plain: {};
		view: {};
	};
	readonly status: 'current';
};

export type WhiteboardsEntrypointExperimentConfigState = {
	hasConfluenceAccess: boolean;
	hasConfluenceSpaces: boolean;
	hasXFlowFromWhiteboardsTouchpoint: boolean;
	eligibility: {
		flow: 'coUse' | 'crossFlow' | null;
		cohort: WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS | 'not-enrolled';
	};
	userPropertyKeys: string[] | null;
};
